import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { playSuccessSound, playErrorSound, playInfoSound } from '../hooks/useSound';

type ToastType = 'success' | 'error' | 'info' | 'loading';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  toast: {
    success: (msg: string, duration?: number) => void;
    error:   (msg: string, duration?: number) => void;
    info:    (msg: string, duration?: number) => void;
    loading: (msg: string) => number;
    dismiss: (id: number) => void;
  };
}

const ToastContext = createContext<ToastContextType | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const add = useCallback((message: string, type: ToastType, duration = 3500): number => {
    const id = ++counterRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
    }
    return id;
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = {
    success: (msg: string, duration?: number) => { playSuccessSound(); add(msg, 'success', duration); },
    error:   (msg: string, duration?: number) => { playErrorSound();   add(msg, 'error',   duration ?? 4500); },
    info:    (msg: string, duration?: number) => { playInfoSound();    add(msg, 'info',    duration); },
    loading: (msg: string)                    => add(msg, 'loading', 0),
    dismiss,
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}

      {/* Snackbar — esquina superior derecha, amarillo con texto oscuro */}
      <div className="fixed top-5 right-5 z-[9998] flex flex-col gap-2 items-end pointer-events-none">
        {toasts.map(t => (
          <div
            key={t.id}
            className="pointer-events-auto flex items-center gap-3 pl-4 pr-3 py-3 rounded-xl shadow-xl text-sm font-semibold max-w-xs
              bg-[#FACF52] text-[#3B4155]
              animate-in slide-in-from-right-4 fade-in duration-200"
          >
            {/* Ícono según tipo */}
            {t.type === 'success' && (
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {t.type === 'error' && (
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {t.type === 'info' && (
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z" />
              </svg>
            )}
            {t.type === 'loading' && (
              <div className="w-4 h-4 flex-shrink-0 animate-spin rounded-full border-2 border-[#3B4155] border-t-transparent" />
            )}

            <span className="flex-1 leading-snug">{t.message}</span>

            <button
              onClick={() => dismiss(t.id)}
              className="ml-1 flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx.toast;
};
